import { i18nFilter } from "app/filters/i18nFilter";
import type { FunctionComponent } from "react";
import { useCallback, useMemo, useRef } from "react";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { getAgeBarChartConfig } from "./ageBarChartConfig";
import { TitleContainer, StyledHeaderTitle, AgeChartContainer, StyledChart, LoaderContainer } from "./StyledComponents";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import { ButtonsContainer } from "pro-features/components/Rule/src/styledComponents";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { PdfExportService } from "app/services/PdfExportService";
import { allTrackers } from "app/services/track/track";
import { TableNoData } from "app/components/React/Table/FlexTable/Big/FlexTableStatelessComponents";
import { Loader } from "app/pages/website-analysis/traffic-sources/mmx/components/common/Loader";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { getExcelDownloadTracking, getExelLink } from "app/pages/website-analysis/demographics/utils";
import { swSettings } from "app/common/services/swSettings";
import type { IAgeDistribution } from "app/pages/website-analysis/demographics/types";

interface IAgeDistributionBarChartProps {
    ageDistribution: IAgeDistribution;
    isLoading?: boolean;
    disableAddToDashboard?: boolean;
    downloadExcelUrl?: string;
}

export const AgeDistributionBarChart: FunctionComponent<IAgeDistributionBarChartProps> = (props) => {
    const { isLoading = false, ageDistribution, disableAddToDashboard = false, downloadExcelUrl } = props;
    const downloadExcelPermitted = swSettings.current.resources.IsExcelAllowed;
    const chartRef = useRef<HTMLDivElement>(null); // for the png

    const params = proNavigator.getParams() as any;
    const apiParams = proNavigator.getApiParams(params);

    const chartConfig = useMemo(() => {
        return getAgeBarChartConfig({ type: "column" });
    }, []);

    // check if the data is empty:
    const isEmpty = useMemo(() => {
        if (!ageDistribution) {
            return true;
        }

        let isEmptyFlag = true;
        Object.values(ageDistribution).map((val) => {
            if (val) {
                isEmptyFlag = false;
            }
        });
        return isEmptyFlag;
    }, [ageDistribution]);

    const getPNG = useCallback((): Promise<void> => {
        allTrackers.dispatchTrackEvent("Age_Distribution.png", "submit-ok", "Age_Distribution.png");
        const offSetX = 0;
        const offSetY = 50;
        const fileName = `Website Analysis Age-distribution-(${apiParams.key})-(from: ${apiParams.from})-(to: ${apiParams.to})`;
        const styleHTML = PdfExportService.getStylesheetsForPng();
        return PdfExportService.downloadHtmlPngFedService(
            styleHTML + chartRef.current.outerHTML,
            fileName,
            chartRef.current.offsetWidth + offSetX,
            chartRef.current.offsetHeight + offSetY,
        );
    }, [chartRef]);

    const chartData = useMemo(() => {
        if (!ageDistribution) {
            return;
        }

        const augmentedProps = Object.values(ageDistribution).map((val) => {
            return { y: val, color: "#4f8df9" };
        });

        return [{ data: augmentedProps }];
    }, [ageDistribution]);

    const getUtilityButtons = () => {
        return (
            <ButtonsContainer>
                <DownloadButtonMenu
                    PNG={true}
                    handleDownloadFunction={getPNG}
                />
                {!disableAddToDashboard && (
                    <AddToDashboard
                        modelType="fromWebsite"
                        webSource={"Total"}
                        type="BarChartDemographics"
                        metric={"WebDemographicsAge"}
                    />
                )}
                <DownloadButtonMenu
                    Excel={true}
                    downloadUrl={downloadExcelUrl ?? getExelLink(params)}
                    excelLocked={!downloadExcelPermitted}
                    trackingFunction={getExcelDownloadTracking("Age")}
                />
            </ButtonsContainer>
        );
    };

    return (
        <AgeChartContainer ref={chartRef}>
            <TitleContainer>
                <FlexColumn>
                    <StyledHeaderTitle>
                        <BoxTitle tooltip={i18nFilter()("webdemographics.bar.title.tooltip")}>
                            {i18nFilter()("dashboard.metricGallery.Website.WebDemographicsAge.title")}
                        </BoxTitle>
                    </StyledHeaderTitle>
                </FlexColumn>
                {!isEmpty && getUtilityButtons()}
            </TitleContainer>
            {isLoading ? (
                <LoaderContainer>
                    <Loader />
                </LoaderContainer>
            ) : (
                <>
                    {isEmpty ? (
                        <TableNoData
                            icon="no-data"
                            messageTitle={i18nFilter()("global.nodata.notavilable")}
                        />
                    ) : (
                        <StyledChart
                            type={"column"}
                            data={chartData}
                            config={chartConfig}
                        />
                    )}
                </>
            )}
        </AgeChartContainer>
    );
};
